import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Truck, Plus, Loader2, AlertTriangle, Calendar, Clock, Image as ImageIcon, Camera, RefreshCw, FileText, CheckCircle, Search, ToggleLeft, ToggleRight, Trash2
} from 'lucide-react';

const LogisticsDashboard = () => {
    const [cameras, setCameras] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    // Formulario de Registro Manual
    const [showManualForm, setShowManualForm] = useState(false);
    const [formData, setFormData] = useState({
        camera_id: '',
        vehicle_type: 'camion_concreto',
        event_type: 'entry',
        license_plate: '',
        notes: ''
    });

    const userRole = localStorage.getItem('userRole') || 'VIEWER';
    const isSuperAdmin = userRole === 'SUPER_ADMIN';

    // Formato de URLs para imágenes
    const formatImageUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        return `http://localhost:4000${path.startsWith('/') ? '' : '/'}${path}`;
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const projectId = localStorage.getItem('activeProjectId');
            const headers = { 
                Authorization: `Bearer ${token}`,
                'x-project-id': projectId
            };

            const [camerasRes, logsRes] = await Promise.all([
                axios.get('http://localhost:4000/api/cameras', { headers }),
                axios.get('http://localhost:4000/api/logistics', { headers })
            ]);

            setCameras(camerasRes.data);
            setLogs(logsRes.data);
        } catch (err) {
            console.error("Error al cargar datos de logística:", err);
            setErrorMessage("No se pudieron cargar los datos de logística de accesos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Alternar el flag de cámara de acceso (is_gate_camera)
    const handleToggleGateCamera = async (camera) => {
        if (!isSuperAdmin) {
            setErrorMessage("Solo el Super Administrador puede designar cámaras de acceso.");
            setTimeout(() => setErrorMessage(null), 3000);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const projectId = localStorage.getItem('activeProjectId');
            
            const updatedStatus = !camera.is_gate_camera;

            await axios.put(`http://localhost:4000/api/cameras/${camera.id}`, {
                is_gate_camera: updatedStatus
            }, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'x-project-id': projectId
                }
            });

            // Actualizar localmente
            setCameras(prev => prev.map(c => c.id === camera.id ? { ...c, is_gate_camera: updatedStatus } : c));
            setSuccessMessage(`Cámara '${camera.name}' ${updatedStatus ? 'asignada como' : 'removida de'} acceso.`);
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error("Error al actualizar la cámara:", err);
            setErrorMessage("Error al actualizar la configuración de la cámara.");
            setTimeout(() => setErrorMessage(null), 3000);
        }
    };

    // Enviar registro manual
    const handleManualSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const token = localStorage.getItem('token');
            const projectId = localStorage.getItem('activeProjectId');

            const res = await axios.post('http://localhost:4000/api/logistics', formData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'x-project-id': projectId
                }
            });

            setSuccessMessage("Acceso registrado exitosamente.");
            setLogs(prev => [res.data, ...prev]);
            setShowManualForm(false);
            setFormData({
                camera_id: '',
                vehicle_type: 'camion_concreto',
                event_type: 'entry',
                license_plate: '',
                notes: ''
            });
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error("Error al registrar acceso:", err);
            setErrorMessage(err.response?.data?.error || "Error al guardar el registro.");
        } finally {
            setSubmitting(false);
        }
    };

    // Simulador de detección de IA para demostraciones
    const handleSimulateAIDetection = async () => {
        setSubmitting(true);
        setErrorMessage(null);
        setSuccessMessage(null);

        const vehicles = ['camion_concreto', 'volteo', 'excavadora', 'camioneta', 'auto'];
        const events = ['entry', 'exit'];
        const randomVehicle = vehicles[Math.floor(Math.random() * vehicles.length)];
        const randomEvent = events[Math.floor(Math.random() * events.length)];
        const randomConfidence = (0.82 + Math.random() * 0.17).toFixed(4); // 82% a 99%
        const plates = ['MX-982-S', 'DF-102-K', 'NL-554-W', 'QRO-887-A', 'EDOMEX-772-S'];
        const randomPlate = plates[Math.floor(Math.random() * plates.length)];

        // Buscar si hay alguna cámara designada como de acceso
        const gateCams = cameras.filter(c => c.is_gate_camera);
        const camId = gateCams.length > 0 ? gateCams[Math.floor(Math.random() * gateCams.length)].id : null;

        try {
            const token = localStorage.getItem('token');
            const projectId = localStorage.getItem('activeProjectId');

            const res = await axios.post('http://localhost:4000/api/logistics', {
                camera_id: camId,
                vehicle_type: randomVehicle,
                event_type: randomEvent,
                confidence: randomConfidence,
                license_plate: randomPlate,
                notes: 'Simulado por motor de Inteligencia Artificial Archi Vision.'
            }, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'x-project-id': projectId
                }
            });

            setSuccessMessage(`Detección de IA Simulada: ${randomVehicle === 'camion_concreto' ? 'Olla de Concreto' : randomVehicle} detectada con ${Math.round(randomConfidence * 100)}% de confianza.`);
            setLogs(prev => [res.data, ...prev]);
            setTimeout(() => setSuccessMessage(null), 4000);
        } catch (err) {
            console.error("Error al simular detección de IA:", err);
            setErrorMessage("Error al conectar con el simulador de IA.");
        } finally {
            setSubmitting(false);
        }
    };

    const getVehicleLabel = (type) => {
        switch (type) {
            case 'camion_concreto': return 'Olla de Concreto';
            case 'volteo': return 'Camión de Volteo';
            case 'excavadora': return 'Excavadora / Retro';
            case 'camioneta': return 'Camioneta de Suministros';
            case 'auto': return 'Vehículo Particular';
            default: return type;
        }
    };

    const filteredLogs = logs.filter(log => {
        const query = searchTerm.toLowerCase();
        return (
            log.vehicle_type.toLowerCase().includes(query) ||
            (log.notes && log.notes.toLowerCase().includes(query)) ||
            (log.license_plate && log.license_plate.toLowerCase().includes(query)) ||
            (log.camera_name && log.camera_name.toLowerCase().includes(query))
        );
    });

    const gateCameras = cameras.filter(c => c.is_gate_camera);

    return (
        <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-8">
            {/* Cabecera */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black flex items-center gap-3 text-theme-text-primary tracking-tight">
                        <Truck className="text-blue-500" size={32} /> Logística y Control de Accesos
                    </h1>
                    <p className="text-sm text-theme-text-secondary mt-1 max-w-2xl">
                        Monitorea el tráfico de maquinaria pesada, vehículos de insumos y personal en los accesos de la obra.
                    </p>
                </div>
                
                <div className="flex items-center gap-2">
                    <button 
                        onClick={handleSimulateAIDetection}
                        disabled={submitting}
                        className="bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 border border-purple-500/30 font-bold px-4 py-2.5 rounded-xl text-xs transition-all flex items-center gap-2"
                    >
                        <RefreshCw size={15} className={submitting ? "animate-spin" : ""} /> Simular Detección IA
                    </button>

                    <button 
                        onClick={() => setShowManualForm(!showManualForm)}
                        className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-4 py-2.5 rounded-xl text-xs transition-all flex items-center gap-1.5 shadow-lg shadow-blue-500/10"
                    >
                        <Plus size={16} /> Registrar Acceso
                    </button>
                </div>
            </div>

            {/* Mensajes de Feedback */}
            {successMessage && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm p-4 rounded-2xl flex items-center gap-3 animate-fade-in">
                    <CheckCircle size={20} className="shrink-0" />
                    <span>{successMessage}</span>
                </div>
            )}
            {errorMessage && (
                <div className="bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm p-4 rounded-2xl flex items-center gap-3 animate-fade-in">
                    <AlertTriangle size={20} className="shrink-0" />
                    <span>{errorMessage}</span>
                </div>
            )}

            {/* Formulario Manual Modal/Expandible */}
            {showManualForm && (
                <div className="bg-theme-panel border border-theme-border rounded-3xl p-6 shadow-xl animate-fade-in">
                    <h3 className="text-lg font-bold mb-4 flex items-center gap-2 border-b border-theme-border pb-3">
                        <FileText size={18} className="text-blue-400" /> Registrar Movimiento de Vehículo Manualmente
                    </h3>
                    <form onSubmit={handleManualSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase">Cámara Asociada</label>
                            <select
                                value={formData.camera_id}
                                onChange={(e) => setFormData({ ...formData, camera_id: e.target.value })}
                                className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none"
                            >
                                <option value="">-- Ninguna / Registro Manual --</option>
                                {gateCameras.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase">Tipo de Vehículo</label>
                            <select
                                value={formData.vehicle_type}
                                onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                                className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none"
                            >
                                <option value="camion_concreto">Olla de Concreto</option>
                                <option value="volteo">Camión de Volteo</option>
                                <option value="excavadora">Excavadora / Retro</option>
                                <option value="camioneta">Camioneta de Insumos</option>
                                <option value="auto">Vehículo Particular</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase">Sentido / Movimiento</label>
                            <select
                                value={formData.event_type}
                                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                                className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none"
                            >
                                <option value="entry">Entrada a Obra</option>
                                <option value="exit">Salida de Obra</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase">Placas / Matrícula</label>
                            <input
                                type="text"
                                value={formData.license_plate}
                                onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                                placeholder="Ej. MX-982-S"
                                className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none"
                            />
                        </div>

                        <div className="space-y-2 md:col-span-2">
                            <label className="text-xs font-bold text-theme-text-secondary uppercase">Notas / Observaciones</label>
                            <input
                                type="text"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Ej. Concreto premezclado de CEMEX, remisión #4492"
                                className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none"
                            />
                        </div>

                        <div className="md:col-span-3 flex justify-end gap-2 pt-2 border-t border-theme-border">
                            <button
                                type="button"
                                onClick={() => setShowManualForm(false)}
                                className="px-4 py-2 text-theme-text-secondary hover:text-theme-text-primary text-xs font-bold"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold px-6 py-2 rounded-xl text-xs flex items-center gap-1.5"
                            >
                                {submitting && <Loader2 size={13} className="animate-spin" />}
                                Guardar Acceso
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* SECCIÓN CÁMARAS DE ACCESO */}
            <div className="bg-theme-panel border border-theme-border rounded-3xl p-6 shadow-xl space-y-4">
                <h2 className="text-lg font-bold flex items-center gap-2 text-theme-text-primary">
                    <Camera className="text-blue-400" size={20} /> Designación de Cámaras de Acceso
                </h2>
                <p className="text-xs text-theme-text-secondary">
                    Las cámaras marcadas como "Cámara de Acceso" serán monitoreadas por el motor de IA para la detección y registro de vehículos en tiempo real. {isSuperAdmin ? "Como Super Admin, puedes activar/desactivar el monitoreo haciendo clic en el interruptor." : ""}
                </p>

                {loading ? (
                    <div className="flex justify-center items-center py-6">
                        <Loader2 className="animate-spin text-blue-500" size={24} />
                    </div>
                ) : cameras.length === 0 ? (
                    <div className="text-center p-6 text-zinc-500 italic">No hay cámaras instaladas en este proyecto.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {cameras.map(camera => (
                            <div key={camera.id} className={`p-4 rounded-2xl border transition-all flex items-center justify-between ${
                                camera.is_gate_camera 
                                ? 'bg-blue-500/5 border-blue-500/30' 
                                : 'bg-theme-bg border-theme-border'
                            }`}>
                                <div className="space-y-1">
                                    <div className="text-xs font-bold text-theme-text-primary">{camera.name}</div>
                                    <div className="text-[10px] text-theme-text-secondary">{camera.location_description || 'Sin ubicación registrada'}</div>
                                </div>
                                <button
                                    onClick={() => handleToggleGateCamera(camera)}
                                    className={`p-1 rounded-full transition-colors ${!isSuperAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    title={isSuperAdmin ? "Alternar cámara de acceso" : "Permiso requerido"}
                                >
                                    {camera.is_gate_camera ? (
                                        <ToggleRight size={32} className="text-blue-500" />
                                    ) : (
                                        <ToggleLeft size={32} className="text-zinc-600" />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* SECCIÓN LISTA DE LOGS */}
            <div className="bg-theme-panel border border-theme-border rounded-3xl p-6 shadow-xl space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-theme-border pb-4">
                    <h2 className="text-lg font-bold flex items-center gap-2 text-theme-text-primary">
                        <FileText className="text-blue-400" size={20} /> Historial de Tránsito de Maquinaria
                    </h2>

                    {/* Buscador */}
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-text-secondary" size={16} />
                        <input
                            type="text"
                            placeholder="Buscar en el registro..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-theme-bg border border-theme-border rounded-xl pl-9 pr-4 py-2 text-xs text-theme-text-primary focus:outline-none focus:border-blue-500"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="animate-spin text-blue-500" size={32} />
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="text-center py-16 text-zinc-500">
                        <Truck size={48} className="mx-auto text-zinc-700 mb-3" />
                        <div className="text-sm font-semibold">No se encontraron registros de accesos</div>
                        <p className="text-xs text-zinc-600 mt-1">Registra un acceso manual o inicia el simulador de IA para cargar logs.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-theme-border text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider">
                                    <th className="pb-3 pl-4">Evento / Vehículo</th>
                                    <th className="pb-3">Cámara / Zona</th>
                                    <th className="pb-3">Placas / ID</th>
                                    <th className="pb-3">Fecha y Hora</th>
                                    <th className="pb-3">Detalles / Notas</th>
                                    <th className="pb-3 pr-4 text-right">Confianza</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-theme-border/50 text-xs">
                                {filteredLogs.map(log => (
                                    <tr key={log.id} className="hover:bg-theme-bg/30 transition-colors group">
                                        <td className="py-4 pl-4 flex items-center gap-3">
                                            <div className={`p-2.5 rounded-xl ${
                                                log.event_type === 'entry' 
                                                ? 'bg-emerald-500/10 text-emerald-400' 
                                                : 'bg-indigo-500/10 text-indigo-400'
                                            }`}>
                                                <Truck size={18} />
                                            </div>
                                            <div>
                                                <div className="font-bold text-theme-text-primary flex items-center gap-1.5">
                                                    {getVehicleLabel(log.vehicle_type)}
                                                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-extrabold uppercase ${
                                                        log.event_type === 'entry'
                                                        ? 'bg-emerald-500/10 text-emerald-400'
                                                        : 'bg-indigo-500/10 text-indigo-400'
                                                    }`}>
                                                        {log.event_type === 'entry' ? 'Entrada' : 'Salida'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 text-theme-text-secondary font-medium">
                                            {log.camera_name || 'Registro Manual'}
                                        </td>
                                        <td className="py-4 font-mono text-[10px] font-bold text-zinc-400">
                                            {log.license_plate || 'S/N'}
                                        </td>
                                        <td className="py-4 text-theme-text-secondary">
                                            <div className="flex items-center gap-1"><Calendar size={12} /> {new Date(log.timestamp).toLocaleDateString()}</div>
                                            <div className="flex items-center gap-1 text-[10px] text-zinc-500 mt-0.5"><Clock size={12} /> {new Date(log.timestamp).toLocaleTimeString()}</div>
                                        </td>
                                        <td className="py-4 text-theme-text-secondary max-w-xs truncate">
                                            {log.notes || '-'}
                                        </td>
                                        <td className="py-4 pr-4 text-right">
                                            {log.confidence && log.confidence < 1.0000 ? (
                                                <span className={`font-bold font-mono px-2 py-0.5 rounded-lg ${
                                                    log.confidence >= 0.90 
                                                    ? 'bg-emerald-500/10 text-emerald-400' 
                                                    : 'bg-amber-500/10 text-amber-400'
                                                }`}>
                                                    {Math.round(log.confidence * 100)}%
                                                </span>
                                            ) : (
                                                <span className="text-zinc-600 font-medium">Manual</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LogisticsDashboard;
